// EditHistoryModal - 編集履歴 Modal（spec.md §5.8.4）
// 右側 EditHistoryButton から開かれ、editHistory の全件をスクロール表示する。

import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { Dialog } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import { RootState } from "app/store";
import I18n from "common/i18n";
import { FadeTransition } from "common/elements";
import { useScale, usePlaySound, btnDelay } from "common/utils";

const MODAL_WIDTH = 720;
const MODAL_HEIGHT = 720;

export const EditHistoryModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const play = usePlaySound();
  const { scale } = useScale();
  const editHistory = useSelector((state: RootState) => state.builder.editHistory);

  const handleClose = () => {
    play();
    btnDelay(() => onClose());
  };

  const entries = [...editHistory].reverse();

  return (
    <Dialog
      open={open}
      maxWidth={false}
      sx={{ zIndex: 100000 }}
      TransitionComponent={FadeTransition}
      componentsProps={{
        backdrop: { style: { backgroundColor: "transparent" } },
      }}
      PaperProps={{
        style: { margin: 0, overflow: "visible", backgroundColor: "transparent" },
        elevation: 0,
      }}
    >
      <div
        className="flex-col-el flex-center w-full h-full bg-textcolor-black/50"
        onClick={handleClose}
      >
        <div
          className="flex-col-el origin-center"
          style={{ transform: `scale(${scale})` }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="flex-col-el bg-white rounded-2xl border-4 border-white shadow-2xl overflow-hidden"
            style={{ width: MODAL_WIDTH, height: MODAL_HEIGHT }}
          >
            {/* ヘッダー */}
            <div className="flex-row-el items-center justify-between gap-3 px-6 py-4 bg-amber-500">
              <div className="flex-row-el items-center gap-3">
                <span className="flex-col-el flex-center type-title text-white">
                  <HistoryIcon fontSize="inherit" />
                </span>
                <div className="flex-col-el items-start">
                  <span className="type-title text-white">
                    {I18n.t("MSG_BUILDER_EDIT_HISTORY")}
                  </span>
                  <span className="type-sm text-white">{entries.length}</span>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="flex-col-el flex-center type-title text-white opacity-button type-inherit-font"
              >
                <CloseIcon fontSize="inherit" />
              </button>
            </div>

            {/* 本体 */}
            {entries.length === 0 ? (
              <div className="flex-col-el flex-center flex-1">
                <span className="type-sm text-gray-400">
                  {I18n.t("MSG_BUILDER_EDIT_HISTORY_EMPTY")}
                </span>
              </div>
            ) : (
              <div className="flex-col-el items-stretch gap-2 flex-1 overflow-y-auto kid-scrollbar p-4">
                {entries.map((entry) => (
                  <div
                    key={entry.seq}
                    className="flex-col-el items-stretch gap-2 bg-amber-50 rounded-xl p-3"
                  >
                    <div className="flex-row-el items-center gap-3">
                      <span className="flex-col-el flex-center shrink-0 rounded-full bg-amber-200 px-2 type-sm text-amber-800 whitespace-nowrap">
                        {`#${entry.seq} v.${entry.version}`}
                      </span>
                      <span className="type-sm text-textcolor-black flex-1 min-w-0 whitespace-nowrap">
                        {entry.action_type}
                      </span>
                      <span className="type-sm text-gray-500 shrink-0 whitespace-nowrap">
                        {dayjs(entry.created_at).format("YYYY-MM-DD HH:mm:ss")}
                      </span>
                    </div>
                    {entry.payload && Object.keys(entry.payload).length > 0 && (
                      <div className="flex-row-el flex-wrap gap-2">
                        {Object.entries(entry.payload).map(([k, v]) => (
                          <span
                            key={k}
                            className="type-sm text-gray-600 bg-white rounded px-2 py-1 whitespace-nowrap"
                          >
                            {`${k}: ${String(v)}`}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
};
